import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { CheckCircle, XCircle, Clock, Search } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  present: "bg-emerald-50 text-emerald-700",
  absent: "bg-red-50 text-red-700",
  excused: "bg-amber-50 text-amber-700",
  late: "bg-orange-50 text-orange-700",
};

export const AttendancePanel = ({ getAuthHeader, sessions = [], participants = [] }) => {
  const [records, setRecords] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedSession, setSelectedSession] = useState("");
  const [markDate, setMarkDate] = useState(new Date().toISOString().split("T")[0]);
  const [bulkRecords, setBulkRecords] = useState([]);

  const fetchRecords = async () => {
    try {
      const res = await axios.get(`${API}/admin/attendance`, getAuthHeader());
      setRecords(res.data);
      setLoaded(true);
    } catch { toast.error("Failed to load attendance"); }
  };

  if (!loaded) fetchRecords();

  const initBulkMark = () => {
    if (!selectedSession) { toast.error("Select a session first"); return; }
    const existing = records.filter(r => r.session_id === selectedSession && r.date === markDate);
    const mapped = participants.map(p => {
      const ex = existing.find(e => e.participant_id === p.id);
      return { participant_id: p.id, name: `${p.first_name} ${p.last_name}`, status: ex?.status || "present", hours: ex?.hours || 2 };
    });
    setBulkRecords(mapped);
  };

  const handleBulkSubmit = async () => {
    try {
      await axios.post(`${API}/admin/attendance/bulk`, {
        session_id: selectedSession,
        date: markDate,
        records: bulkRecords.map(r => ({ participant_id: r.participant_id, status: r.status, hours: r.hours })),
      }, getAuthHeader());
      toast.success("Attendance saved");
      setBulkRecords([]);
      fetchRecords();
    } catch { toast.error("Failed to save attendance"); }
  };

  const updateBulkStatus = (idx, status) => {
    const updated = [...bulkRecords];
    updated[idx].status = status;
    updated[idx].hours = status === "present" || status === "late" ? 2 : 0;
    setBulkRecords(updated);
  };

  const filtered = records.filter(r =>
    `${r.participant_name} ${r.session_title} ${r.date}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      {/* Mark Attendance Section */}
      <div className="bg-gray-50 rounded-xl p-4 space-y-3">
        <h4 className="text-sm font-semibold text-gray-700">Mark Attendance</h4>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="min-w-[200px]">
            <Label className="text-xs">Session</Label>
            <select data-testid="attendance-session" value={selectedSession} onChange={e => setSelectedSession(e.target.value)} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm">
              <option value="">Select session...</option>
              {sessions.map(s => <option key={s.id} value={s.id}>{s.title} ({s.day_of_week})</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Date</Label>
            <Input data-testid="attendance-date" type="date" value={markDate} onChange={e => setMarkDate(e.target.value)} className="mt-1" />
          </div>
          <Button data-testid="attendance-load-btn" onClick={initBulkMark} className="bg-warm-700 hover:bg-warm-800 text-white">
            Load Roster
          </Button>
        </div>

        {bulkRecords.length > 0 && (
          <div className="mt-3 space-y-2">
            {bulkRecords.map((r, i) => (
              <div key={r.participant_id} className="flex items-center gap-3 bg-white p-3 rounded-lg">
                <span className="text-sm font-medium flex-1">{r.name}</span>
                <div className="flex gap-1">
                  {["present", "late", "excused", "absent"].map(s => (
                    <button key={s} onClick={() => updateBulkStatus(i, s)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${r.status === s ? statusColors[s] : "bg-gray-100 text-gray-400 hover:bg-gray-200"}`}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <Button data-testid="attendance-save-btn" onClick={handleBulkSubmit} className="bg-warm-700 hover:bg-warm-800 text-white mt-2">
              Save Attendance ({bulkRecords.length} participants)
            </Button>
          </div>
        )}
      </div>

      {/* Records Table */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search records..." className="pl-10" />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-gray-100">
            <th className="text-left py-3 px-4 text-gray-500 font-medium">Participant</th>
            <th className="text-left py-3 px-4 text-gray-500 font-medium hidden sm:table-cell">Session</th>
            <th className="text-left py-3 px-4 text-gray-500 font-medium">Date</th>
            <th className="text-left py-3 px-4 text-gray-500 font-medium">Status</th>
            <th className="text-right py-3 px-4 text-gray-500 font-medium">Hours</th>
          </tr></thead>
          <tbody>
            {filtered.slice(0, 100).map(r => (
              <tr key={r.id} className="border-b border-gray-50">
                <td className="py-3 px-4 font-medium text-gray-900">{r.participant_name}</td>
                <td className="py-3 px-4 text-gray-600 hidden sm:table-cell">{r.session_title}</td>
                <td className="py-3 px-4 text-gray-600">{r.date}</td>
                <td className="py-3 px-4">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColors[r.status]}`}>{r.status}</span>
                </td>
                <td className="py-3 px-4 text-right text-gray-600">{r.hours}h</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="py-8 text-center text-gray-400">No attendance records</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
